import { formatDisplayName } from '@/common/utils/formatDisplayName';
import type { IdentityRequest } from '$api-clients/hooks';
import type { AgentProfileSummaryFields } from './types';

type TeamMember = IdentityRequest<'/team-members'>['Data']['data'][number];

/**
 * Subset of the `/team-members` row shared by every team-member response the
 * app maps to an agent profile: the interaction-permissions team-members list
 * (with `expand=team`), the team-member detail, and session-enriched
 * self-membership rows.
 */
export type AgentProfileTeamMemberInput = Pick<
  TeamMember,
  | 'userId'
  | 'legacyExternalId'
  | 'firstName'
  | 'lastName'
  | 'photoUrl'
  | 'contact'
  | 'roles'
  | 'license'
> & {
  team: Pick<
    NonNullable<TeamMember['team']>,
    'id' | 'legalName' | 'alias' | 'legacyExternalId' | 'brokerageId'
  >;
};

export function mapTeamMemberToAgentProfile(
  member: AgentProfileTeamMemberInput,
): AgentProfileSummaryFields {
  return {
    id: member.legacyExternalId || member.userId,
    userIdentityId: member.userId,
    firstName: member.firstName,
    lastName: member.lastName,
    displayName: formatDisplayName({
      firstName: member.firstName,
      lastName: member.lastName,
      teamName: member.team.legalName ?? undefined,
      teamAlias: member.team.alias,
    }),
    avatar: member.photoUrl ?? null,
    email: member.contact.email ?? '',
    companyName: member.team.legalName ?? null,
    teamId: member.team.legacyExternalId ?? member.team.id,
    teamIdentityId: member.team.id,
    teamName: member.team.legalName ?? null,
    brokerageId: member.team.brokerageId ?? null,
    licenseState: member.license?.state ?? null,
    roles: member.roles,
  };
}
